<script setup>
import {computed, onBeforeMount, onBeforeUnmount} from 'vue'
import { useProjectStore } from '@/stores/project'
import { useGeoStore } from '@/stores/geo'
import {useRoute} from 'vue-router'

import TemplatesList from './TemplatesList.vue'
import TemplatesLibraryList from './TemplatesLibraryList.vue'
import {Button} from '@/components/ui/button'
import {PlusCircle} from 'lucide-vue-next'

const route = useRoute()
const projectId = route.params.project_id

const projectStore = useProjectStore()

const geoStore = useGeoStore()
const project = computed(() => projectStore.current)
const templates = computed(() => {
  const allTemplates = geoStore.reportTemplates
  if (!allTemplates) return null
  return [...allTemplates].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})
const templatesLength = computed(() => templates.value ? templates.value.length : 0)

const libraryTemplates = computed(() => {
  const library = geoStore.systemTemplatesLibrary
  if (!library) return null
  return [...library].sort((a, b) => a.name.localeCompare(b.name))
})
const libraryLength = computed(() => libraryTemplates.value ? libraryTemplates.value.length : 0)

onBeforeMount(async () => {
  await projectStore.fetchProject(projectId)
  await geoStore.fetchProjectReportTemplates(projectId)
  await geoStore.fetchSystemTemplatesLibrary(projectId)
})

onBeforeUnmount(() => {
  geoStore.setReportTemplates(null)
  geoStore.setSystemTemplatesLibrary(null)
})
</script>

<template>
  <div class="mb-1 flex items-center">
    <div class="basis-1/2">
      <h3 class="text-base font-bold">Report Templates ({{templatesLength}})</h3>
    </div>
    <div class="basis-1/2 flex justify-end items-center">
      <router-link
        :to="{ name: 'CreateTemplate', params: { org_id: route.params.org_id, project_id: projectId } }"
        custom
        v-slot="{ href, navigate }"
      >
        <Button as-child
          class="px-2 text-muted-foreground max-w-fit"
          variant="ghost"
          data-testid="create-template-button"
        ><a :href="href" @click="navigate"><PlusCircle /></a></Button>
      </router-link>
    </div>
  </div>
  <TemplatesList v-if="templates" :project="project" :templates="templates" :project-id="Number(projectId)" />

  <!-- Templates Library Section -->
  <div class="mt-8 mb-1 flex items-center">
    <div class="basis-1/2 flex items-center gap-2">
      <h3 class="text-base font-bold">Templates Library ({{libraryLength}})</h3>
    </div>
  </div>
  <p class="text-sm text-muted-foreground mb-3">System templates available to add to your project</p>
  <TemplatesLibraryList
    v-if="libraryTemplates && libraryTemplates.length > 0"
    :templates="libraryTemplates"
    :project-id="Number(projectId)"
  />
  <div v-else-if="libraryTemplates && libraryTemplates.length === 0" class="text-center py-8 text-muted-foreground">
    No system templates available
  </div>
</template>
